import React, { useEffect, useMemo, useState } from 'react';
import { rbacApi } from '../api/rbac.api';
import { Button } from '@/components/ui/button';
import { Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';
import useInLeadershipShell from '@/hooks/useInLeadershipShell';
import { LEADERSHIP_CONTENT } from '@/config/leadershipLayout';
import RbacScreenIntro from '../components/RbacScreenIntro';
import PermissionsRoleList from '../components/PermissionsRoleList';
import PermissionsModuleBlock from '../components/PermissionsModuleBlock';
import { coveragePct } from '../utils/rbacLabels';
import { IITH } from '@/pages/landing/iithBrand';

export default function PermissionsPage() {
  const lead = useInLeadershipShell();
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [selected, setSelected] = useState('');
  const [checked, setChecked] = useState(new Set());
  const [baseline, setBaseline] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const grouped = useMemo(() => {
    const map = new Map();
    permissions.forEach((p) => {
      const m = p.module || 'General';
      if (!map.has(m)) map.set(m, []);
      map.get(m).push(p);
    });
    return Array.from(map.entries());
  }, [permissions]);

  const dirty = useMemo(() => {
    if (checked.size !== baseline.size) return true;
    for (const c of checked) if (!baseline.has(c)) return true;
    return false;
  }, [checked, baseline]);

  const load = async () => {
    setLoading(true);
    try {
      const [r, p] = await Promise.all([
        rbacApi.loadRoles(true),
        rbacApi.listPermissions(),
      ]);
      const perms = p?.length ? p : await rbacApi.loadPermissions();
      setRoles(r);
      setPermissions(perms);
      let focus = '';
      try { focus = sessionStorage.getItem('rbac.focusRoleKey') || ''; sessionStorage.removeItem('rbac.focusRoleKey'); } catch (_) { /* ignore */ }
      const first = focus || selected || r[0]?.role_key || '';
      setSelected(first);
      const role = r.find((x) => x.role_key === first) || r[0];
      const set = new Set(role?.permission_codes || []);
      setChecked(set);
      setBaseline(new Set(set));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load permissions');
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const selectRole = (roleKey) => {
    if (dirty && !window.confirm('Discard unsaved permission changes?')) return;
    setSelected(roleKey);
    const role = roles.find((x) => x.role_key === roleKey);
    const set = new Set(role?.permission_codes || []);
    setChecked(set);
    setBaseline(new Set(set));
  };

  const toggle = (code) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  };

  const toggleModule = (items, turnOn) => {
    setChecked((prev) => {
      const next = new Set(prev);
      items.forEach((p) => {
        if (turnOn) next.add(p.code);
        else next.delete(p.code);
      });
      return next;
    });
  };

  const save = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const updated = await rbacApi.mapPermissions(selected, Array.from(checked));
      setRoles((prev) => prev.map((r) => (r.role_key === selected ? updated : r)));
      setBaseline(new Set(checked));
      toast.success('Permissions saved for role');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  const selectedRole = roles.find((r) => r.role_key === selected);
  const modulesEnabled = grouped.filter(([, items]) => items.some((p) => checked.has(p.code))).length;

  if (loading) {
    return (
      <div className="p-10 text-center text-zinc-500">
        <Loader2 className="h-5 w-5 animate-spin inline" />
      </div>
    );
  }

  const saveBtn = (
    <Button
      onClick={save}
      disabled={saving || !selected || !dirty}
      style={{ background: dirty ? IITH.saffron : undefined }}
      className={dirty ? 'text-white' : ''}
      variant={dirty ? 'default' : 'outline'}
      data-testid="permissions-save"
    >
      {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
      {dirty ? 'Save mapping' : 'Saved'}
    </Button>
  );

  const stats = [
    { label: 'Catalogue', value: permissions.length, hint: `${grouped.length} modules` },
    { label: 'Selected role', value: selectedRole?.name || '—', hint: selectedRole ? 'editing now' : 'pick a role' },
    { label: 'Granted', value: checked.size, hint: `${coveragePct(checked.size, permissions.length || 1)}% coverage` },
    { label: 'Modules on', value: modulesEnabled, hint: dirty ? 'Unsaved changes' : 'In sync' },
  ];

  return (
    <div className={`${lead ? `${LEADERSHIP_CONTENT} space-y-5` : 'p-6 max-w-6xl mx-auto space-y-5'}`} data-testid="permissions-page">
      <RbacScreenIntro
        lead={lead}
        title="Permissions"
        subtitle="Grant capabilities by module. Changes apply to every user on the selected clinic role."
        actions={saveBtn}
        stats={stats}
      />

      <div className="grid lg:grid-cols-12 gap-4">
        <div className="lg:col-span-4">
          <PermissionsRoleList
            roles={roles}
            selected={selected}
            checkedCount={checked.size}
            permissionTotal={permissions.length}
            onSelect={selectRole}
          />
        </div>
        <div className="lg:col-span-8 space-y-3 max-h-[72vh] overflow-y-auto pr-1">
          {!selected ? (
            <p className="text-sm text-zinc-500 p-6">Select a role to map permissions.</p>
          ) : (
            grouped.map(([module, items]) => (
              <PermissionsModuleBlock
                key={module}
                module={module}
                items={items}
                checked={checked}
                onToggle={toggle}
                onToggleModule={toggleModule}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
}
